/**
 * Flat per-provider ROW MODEL for an integration-status payload — shared by the
 * marketplace deploy modal and the deployed-agent settings list so both render
 * exactly ONE switch per provider id.
 *
 * Why this exists: a provider can appear in SEVERAL requirement OR-groups
 * serving different roles. Canonical case — Lark on the copilot/prd agents:
 *   required chat group  { any: [slack, lark] }                     → "Chat"
 *   optional docs group  { any: [google, lark, lark_docs, notion] } → "Docs"
 * Expanding the groups naively draws TWO rows for the same id (one locked +
 * one seemingly optional) — two switches where only ONE thing exists (one id,
 * one connection, one allowlist entry) — and the optional-looking row can't be
 * toggled because the required half locks the shared id.
 *
 * Faithful port of the self-host dashboard's mergeDuplicateIds
 * (backend/selfhosted/ui/index.html — deploy modal + agent-detail settings):
 * expand required+optional entries (id strings + `{ any: [...] }` OR-groups)
 * into one row per member, then MERGE duplicates by provider id:
 *   - optional       = AND of the occurrences (required eats optional)
 *   - connected      = OR  of the occurrences (connected if any is)
 *   - requiredMember = OR  (was any occurrence in the REQUIRED section?)
 *   - roles          = accumulated roles of its role-bearing groups, kept RAW so
 *                      a label can be formed IF one is ever needed (it is needed
 *                      only when two rows would render the same name — see
 *                      mergedRowLabel + collidingProviders)
 * First-occurrence order is preserved.
 */

import { roleForGroup, roleLabel } from './integrationRoles';

const canonicalKey = (arr) => arr.slice().sort().join(',');

// Flat connected ids out of one "connected" list (required's `connected` or
// optional's `optionalConnected`): string entries are connected as-is,
// OR-group entries contribute their `connectedMembers`.
function flattenConnected(list) {
  const out = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    if (typeof entry === 'string') {
      out.add(entry);
    } else if (entry && Array.isArray(entry.connectedMembers)) {
      for (const m of entry.connectedMembers) if (typeof m === 'string') out.add(m);
    }
  }
  return out;
}

/**
 * Build the merged row model.
 *
 * @param {Array<string|{any:string[]}>} reqList   status.required (or template fallback)
 * @param {Array<string|{any:string[]}>} optList   status.optional (or template fallback)
 * @param {?Array} connectedList          status.connected (required section's)
 * @param {?Array} optionalConnectedList  status.optionalConnected
 * @param {{memberOrder?: ?Object<string,number>}} [opts]  optional display order
 *        for OR-group members (e.g. { slack: 0, lark: 1 } — unlisted ids keep
 *        their relative order after the listed ones). Display-only; group
 *        matching stays order-independent (canonical sorted key).
 * @returns {Array<{id:string,connected:boolean,optional:boolean,
 *                  requiredMember:boolean,members:?string[],
 *                  requiredGroup:?string[],roles:string[]}>}
 */
export function buildIntegrationRows(
  reqList,
  optList,
  connectedList,
  optionalConnectedList,
  { memberOrder = null } = {},
) {
  const rows = [];

  const pushEntry = (entry, connected, optionalSection) => {
    if (typeof entry === 'string') {
      rows.push({
        id: entry,
        connected: flattenConnected(connected).has(entry),
        optional: optionalSection,
        requiredMember: !optionalSection,
        members: null,
        // A bare string entry is an integration required (or offered) ON ITS
        // OWN — it belongs to no one-of group, so there is no alternative to
        // name. Explicitly null, never inherited from some other group.
        requiredGroup: null,
      });
      return;
    }
    if (!entry || !Array.isArray(entry.any) || entry.any.length === 0) return;
    // Match the connected group ORDER-INDEPENDENTLY (canonical sorted key on
    // both sides — a display re-sort below must never break matching).
    const key = canonicalKey(entry.any);
    let matched = [];
    for (const c of Array.isArray(connected) ? connected : []) {
      if (c && typeof c === 'object' && Array.isArray(c.any) && canonicalKey(c.any) === key) {
        matched = Array.isArray(c.connectedMembers) ? c.connectedMembers : [];
      }
    }
    // Once ONE member satisfies the group, sibling rows read as optional.
    const groupSatisfied = matched.length > 0;
    const members = memberOrder
      ? entry.any.slice().sort((a, b) => (memberOrder[a] ?? 99) - (memberOrder[b] ?? 99))
      : entry.any;
    for (const mid of members) {
      if (typeof mid !== 'string') continue;
      rows.push({
        id: mid,
        connected: matched.includes(mid),
        optional: optionalSection || groupSatisfied,
        requiredMember: !optionalSection,
        members: entry.any,
        // requiredGroup: the members of the REQUIRED one-of group this row
        // belongs to — the ONE input the "X · one of these" badge reads.
        // Deliberately NOT `members`: a provider can be hard-required by a bare
        // string entry AND appear in an OPTIONAL group, and after the merge
        // `members` would carry that optional group — badging a mandatory
        // integration as "one of these" (i.e. skippable). This field is set
        // only by a group in the REQUIRED section, so it cannot say that.
        requiredGroup: optionalSection ? null : entry.any,
      });
    }
  };

  for (const e of Array.isArray(reqList) ? reqList : []) pushEntry(e, connectedList, false);
  for (const e of Array.isArray(optList) ? optList : []) pushEntry(e, optionalConnectedList, true);

  // MERGE duplicate provider ids across role-groups — ONE switch per provider
  // (one id, one connection, one allowlist entry). Required wins over optional;
  // connected if any occurrence is; role suffixes accumulate.
  const byId = new Map();
  const merged = [];
  for (const r of rows) {
    const role = r.members ? roleForGroup(r.members) : null;
    const ex = byId.get(r.id);
    if (!ex) {
      r.roles = role ? [role] : [];
      byId.set(r.id, r);
      merged.push(r);
      continue;
    }
    ex.optional = ex.optional && r.optional;
    ex.connected = ex.connected || r.connected;
    ex.requiredMember = ex.requiredMember || r.requiredMember;
    // A REQUIRED-group membership from ANY occurrence survives the merge (the
    // same "required wins" rule as `optional`/`requiredMember` above) — a
    // provider whose first occurrence was a bare string entry must still show
    // the required group it also belongs to.
    ex.requiredGroup = ex.requiredGroup || r.requiredGroup;
    if (role && !ex.roles.includes(role)) ex.roles.push(role);
  }
  return merged;
}

/**
 * Display name for one MERGED row — the ONE place a row's label is decided.
 *
 * A qualifier is added ONLY when this row's name collides with another row's in
 * the SAME rendering (`collidingSet`, computed over these very rows) — the sole
 * situation a label can help. Today nothing collides, so every row renders its
 * plain registry name ("Lark Chat", not "Lark Chat · Docs"). On a collision the
 * row's ACCUMULATED roles (every role-bearing group it was merged from) form the
 * clause via roleLabel; a colliding row that serves no role has nothing to add
 * and stays plain.
 *
 * @param {string} baseName          the provider's plain display name
 * @param {{id:string,members:?string[],roles:string[]}} row
 * @param {?Set<string>} collidingSet  collidingProviders() over the SAME rows
 * @returns {string}
 */
export function mergedRowLabel(baseName, row, collidingSet) {
  if (!row || !collidingSet || !collidingSet.has(row.id)) return baseName;
  return roleLabel(baseName, row.roles);
}
